/**
 * Native mobile push registration (Tauri/Android-iOS shell only).
 *
 * On the Tauri mobile shell, after the WS is authenticated we ask the native
 * push plugin (`tauri-plugin-cyborg-push`) for this device's FCM token and
 * register it with the relay (`cyborg:fcm_register`). The relay then delivers a
 * push when the user is offline. No-op on web/desktop (no `window.__TAURI__`).
 *
 * Idempotent: the relay upserts by token, so calling this on every (re)auth is
 * safe. First launch needs a short backoff — Firebase delivers the token a beat
 * after the plugin's load().
 */

// Structural type — only the FCM methods are needed, so we avoid importing the
// concrete client class (and its dependency graph) here.
interface FcmRegistrar {
  registerFcmToken(token: string, platform: string, deviceName?: string): Promise<void>;
}

// Sign-out counterpart. `connected` is read (not just the RPC) so a teardown on a
// dead socket skips the request instead of parking on the client's waitForOpen/
// waitForAuth timeouts — the caller chains `client.disconnect()` off this promise.
interface FcmUnregistrar {
  connected: boolean;
  unregisterFcmToken(token: string): Promise<void>;
}

// Platform detection moved to ./platform.ts (Gemini #946) so non-push code (e.g.
// the terminal's viewport/keyboard gating) can detect the native shell WITHOUT
// importing this push module. Imported here for push-registration gating below and
// re-exported so the existing ~20 importers of these from "./push" keep working.
import { isTauri, isTauriIOS, isTauriAndroid } from "./platform.js";
import { reportUiFailure } from "../report-failure.js";
export { isTauri, isTauriIOS, isTauriAndroid };

// Native-media actions moved to ./native-media.ts (#543) so FCM push and
// media save/copy stay separate concerns. Re-exported so existing importers of
// these from "./push" keep working.
export {
  saveImageToPhotos,
  saveVideoToPhotos,
  saveFile,
  copyImageToClipboardNative,
} from "./native-media.js";

function platformTag(): "android" | "ios" {
  if (typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
    return "ios";
  }
  return "android";
}

// Last token this PROCESS registered. Used only to (a) skip a redundant
// re-register inside the rotation listener and (b) know what to unregister on
// sign-out. It is deliberately NOT consulted to skip registration at auth time:
// see registerMobilePush.
let registeredToken: string | null = null;
// The rotation listener is process-global and must be bound EXACTLY once:
// registerMobilePush runs on every (re)auth, so an unguarded bind stacked a new
// listener per reconnect (N duplicate re-registers per rotation).
let refreshListenerBound = false;

/**
 * Subscribe to Firebase token rotations and re-register the new token with the
 * relay. The native plugin emits this via trigger("fcm-token-refresh", { token }),
 * a PLUGIN event — delivered only to addPluginListener('cyborg-push', …), NOT a
 * global listen() (matches nativeComposer.ts). A bare listen() silently missed
 * rotations.
 *
 * Bound BEFORE the token poll below, never after: the poll backs off up to 18.5s
 * and RETURNS EARLY when no token has arrived by then, so a device on a slow
 * network used to fall out of registerMobilePush before ever reaching this — and
 * then silently ignored every subsequent rotation for the rest of the process.
 * Binding first is also correct on the happy path (a rotation cannot be missed
 * while we are still polling for the initial token).
 */
async function bindTokenRefreshListener(client: FcmRegistrar): Promise<void> {
  if (refreshListenerBound) return;
  try {
    const { addPluginListener } = await import("@tauri-apps/api/core");
    await addPluginListener<{ token?: string } | string>(
      "cyborg-push",
      "fcm-token-refresh",
      async (payload) => {
        const next = typeof payload === "string" ? payload : payload?.token;
        if (!next || next === registeredToken) return;
        try {
          await client.registerFcmToken(next, platformTag(), `${platformTag()} device`);
          registeredToken = next;
        } catch (err) {
          console.warn("[push] FCM refresh register failed", err);
        }
      },
    );
    refreshListenerBound = true;
  } catch {
    // plugin API unavailable (non-Tauri) — ignore.
  }
}

async function fetchFcmToken(): Promise<string | null> {
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const t = await invoke<string | null>("plugin:cyborg-push|get_fcm_token");
    return t ?? null;
  } catch (err) {
    console.warn("[push] get_fcm_token failed", err);
    return null;
  }
}

/**
 * Ensure the OS-level notification permission is granted (Android 13+ / API 33
 * gates notifications behind the POST_NOTIFICATIONS runtime permission, denied
 * by default — without prompting, FCM banners are silently dropped). The
 * tauri-plugin-notification requestPermission drives the native system dialog;
 * because POST_NOTIFICATIONS is app-wide, granting it also unblocks our FCM
 * FirebaseMessagingService banners. On API <33 it's auto-granted (no-op).
 * This is an Android-13+ concern only: iOS prompts natively via
 * UNUserNotificationCenter in the Swift plugin, and a desktop Tauri shell has
 * no such flow — so target Android explicitly (skipping iOS alone would still
 * run on desktop Tauri and could trigger an unexpected prompt / log noise).
 * Denial degrades gracefully: the token still registers; banners stay
 * suppressed until the user enables them.
 */
async function ensureNotificationPermission(): Promise<void> {
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  if (!isAndroid) return;
  try {
    // Invoke tauri-plugin-notification's commands directly (the same ones its JS
    // wrapper calls) — it's bundled natively (lib.rs) and granted via
    // `notification:default` in the capability, so no extra JS dependency is
    // needed. is_permission_granted → boolean; request_permission → the
    // resulting state string ("granted" | "denied" | "default").
    const { invoke } = await import("@tauri-apps/api/core");
    if (await invoke<boolean>("plugin:notification|is_permission_granted")) return;
    const result = await invoke<string>("plugin:notification|request_permission");
    if (result !== "granted") {
      console.log(`[push] notification permission ${result} — banners suppressed until enabled`);
    }
  } catch (err) {
    // Plugin/API unavailable (older shell) — never block token registration.
    console.warn("[push] notification permission request failed", err);
  }
}

/**
 * Poll for the FCM token (first install delivers it ~1-3s after load) and
 * register it with the relay. Safe to call repeatedly — the relay upserts by
 * token (`onConflictDoUpdate` on `fcm_tokens.token`, pg-sync.ts), so a repeat
 * call is one idempotent write.
 *
 * SECURITY: this deliberately registers on EVERY authenticated call, even when
 * the device token is unchanged. It used to early-out on `token ===
 * registeredToken`, and that early-out was the cross-account push leak: the
 * module global survives sign-out (logout is a `goto("/login")`, not a page
 * load), so when user B signed in on the same phone the token matched, the
 * register RPC never fired, and the relay row still mapped this device to user
 * A — whose pushes carry the SENDER NAME AND MESSAGE TEXT (relay
 * `messagePushPreview`). Re-registering costs one idempotent upsert; skipping it
 * costs A's private messages on B's lock screen, so this fails closed: if we
 * cannot prove the registration belongs to the caller's session, we re-register.
 */
export async function registerMobilePush(client: FcmRegistrar): Promise<void> {
  if (!isTauri()) return;
  // Prompt for the notification permission early (first authenticated launch)
  // so banners can show once a push arrives (Android 13+ #468).
  await ensureNotificationPermission();
  // Bind the rotation listener BEFORE the token poll — the poll can return early
  // (see bindTokenRefreshListener's note).
  await bindTokenRefreshListener(client);
  const delaysMs = [0, 1000, 2500, 5000, 10000];
  let token: string | null = null;
  for (const delay of delaysMs) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    token = await fetchFcmToken();
    if (token) break;
  }
  if (!token) {
    console.log("[push] no FCM token yet (Firebase not ready or not configured)");
    return;
  }
  try {
    await client.registerFcmToken(token, platformTag(), `${platformTag()} device`);
    registeredToken = token;
    console.log(`[push] FCM token registered (${token.slice(0, 12)}…)`);
  } catch (err) {
    console.warn("[push] FCM register failed", err);
  }
}

/**
 * Sign-out counterpart to registerMobilePush: drop this device's row from the
 * relay so it stops receiving the ending user's pushes (`cyborg:fcm_unregister`
 * → `deleteFcmToken`). Mirrors the native shell's `PushManager.detach()` →
 * `relay.disconnect()` ordering (AppModel.swift) — the RPC needs the socket that
 * is about to close, so the caller chains the disconnect off this promise.
 *
 * Clearing `registeredToken` happens SYNCHRONOUSLY (before the first await), so
 * it is guaranteed even if the RPC never lands; that plus the unconditional
 * re-register in registerMobilePush is the fail-closed half of the fix, and the
 * unregister is the belt that also covers "A signs out, B never signs in".
 */
export async function unregisterMobilePush(client: FcmUnregistrar): Promise<void> {
  const cached = registeredToken;
  registeredToken = null;
  if (!isTauri() || !client.connected) return;
  // Fall back to asking the plugin: the app may have been relaunched since the
  // registration, leaving the relay row live with nothing cached to name it.
  const token = cached ?? (await fetchFcmToken());
  if (!token) return;
  try {
    await client.unregisterFcmToken(token);
    console.log(`[push] FCM token unregistered (${token.slice(0, 12)}…)`);
  } catch (err) {
    // An unreachable relay must not stall sign-out, but a token left registered keeps
    // pushing the previous account's notifications to this device until re-register.
    reportUiFailure("push.unregister", err);
  }
}
